import { useState } from "react";

import { useAnalytics } from "@accurx/analytics";
import { Ds } from "@accurx/design";
import { QuickViewPortal } from "@accurx/quick-view";
import { ErrorBoundary } from "@accurx/shared";
import { GenericErrorState } from "domains/message-component/components/Compose/components/GenericErrorState/GenericErrorState";
import { ScheduleSendCustomDate } from "domains/message-component/components/Compose/components/ScheduleSend/components/CustomDate/ScheduleSendCustomDate";
import { ScheduleSendItems } from "domains/message-component/components/Compose/components/ScheduleSend/components/ScheduleSendItems/ScheduleSendItems";
import { useCompose } from "domains/message-component/context";

import { StyledErrorContainer } from "./ScheduleSend.styles";
import { ScheduleSendAt } from "./types/ScheduleSend.types";

type ScheduleSendProps = {
    onConfirmScheduleSend: (sendAt: ScheduleSendAt) => void;
};

export const ScheduleSend = ({ onConfirmScheduleSend }: ScheduleSendProps) => {
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const { state } = useCompose();
    const track = useAnalytics();

    return (
        <>
            <Ds.DropdownMenu>
                <Ds.Tooltip>
                    <Ds.Tooltip.Content>Schedule message</Ds.Tooltip.Content>
                    <Ds.Tooltip.Trigger>
                        <Ds.DropdownMenu.Trigger
                            asChild
                            disabled={state.isScheduling}
                        >
                            <Ds.Button
                                disabled={state.isScheduling}
                                size="small"
                                data-userflow-id="compose-schedule-send"
                                aria-label="Scheduled send options"
                                onClick={() => {
                                    track("ConversationSchedule Button Click", {
                                        indexMessage: 0,
                                        templateName:
                                            state.template.value?.title,
                                        scheduleType: state.sendAt
                                            ? "Custom"
                                            : "Now",
                                    });
                                }}
                            >
                                <Ds.Icon
                                    name="Arrow"
                                    color="white"
                                    size="small"
                                    aria-hidden="true"
                                />
                            </Ds.Button>
                        </Ds.DropdownMenu.Trigger>
                    </Ds.Tooltip.Trigger>

                    <Ds.DropdownMenu.Content align="end">
                        <Ds.DropdownMenu.Header>
                            <Ds.Text size="small" weight="bold">
                                Schedule message
                            </Ds.Text>
                        </Ds.DropdownMenu.Header>
                        <ErrorBoundary
                            fallback={
                                <StyledErrorContainer>
                                    <GenericErrorState>
                                        <Ds.Text>
                                            Sorry, something went wrong. Refresh
                                            the page to try again.
                                        </Ds.Text>
                                    </GenericErrorState>
                                </StyledErrorContainer>
                            }
                        >
                            <ScheduleSendItems
                                onConfirmScheduleSend={onConfirmScheduleSend}
                                onClickCustomDate={() =>
                                    setIsQuickViewOpen(true)
                                }
                            />
                        </ErrorBoundary>
                    </Ds.DropdownMenu.Content>
                </Ds.Tooltip>
            </Ds.DropdownMenu>

            <QuickViewPortal
                isOpen={isQuickViewOpen}
                onClose={() => setIsQuickViewOpen(false)}
            >
                <ScheduleSendCustomDate
                    onConfirm={(sendAt: ScheduleSendAt) => {
                        onConfirmScheduleSend(sendAt);
                        setIsQuickViewOpen(false);
                    }}
                    sendAt={state.sendAt}
                />
            </QuickViewPortal>
        </>
    );
};
